/** **`sessionStorage`** prefix for invitation bearer secrets keyed by quiz id. */
export const PARTICIPANT_INVITATION_TOKEN_SS_PREFIX =
  "participant-quiz:v1:invitation-token-for-quiz:";

/**
 * Builds the storage key for a participant invitation token scoped to one quiz.
 *
 * @param quizId - Published quiz UUID from the **`/quiz/{uuid}`** path.
 */
export function participantInvitationTokenStorageKey(quizId: string): string {
  return `${PARTICIPANT_INVITATION_TOKEN_SS_PREFIX}${quizId}`;
}

/**
 * Obtains the invitation token from the URL query or **`sessionStorage`**, persists
 * URL tokens for reloads in this tab, and removes **`token`** from the query string
 * via **`history.replaceState`** so it does not remain visible in the address bar.
 *
 * Non-token query parameters are preserved when stripping **`token`**.
 *
 * @param quizId - Published quiz UUID from the **`/quiz/{uuid}`** path.
 * @returns Trimmed bearer secret or empty string when absent.
 */
export function consumeParticipantInvitationToken(quizId: string): string {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("token")?.trim() ?? "";
  const key = participantInvitationTokenStorageKey(quizId);

  if (fromUrl.length > 0) {
    try {
      window.sessionStorage.setItem(key, fromUrl);
    } catch {
      /* ignore quota or privacy mode */
    }
    params.delete("token");
    const tail = params.toString();
    const cleanUrl =
      window.location.pathname +
      (tail.length > 0 ? `?${tail}` : "") +
      window.location.hash;
    window.history.replaceState(window.history.state, "", cleanUrl);
    return fromUrl;
  }

  try {
    return window.sessionStorage.getItem(key)?.trim() ?? "";
  } catch {
    return "";
  }
}
